import React, { useState } from 'react';
import { DynamicForm } from '@omilia/chat-ui';
import { colors } from '@omilia/theme';

import ChatHeader from '../ChatHeader';
import ChatContent from '../ChatContent';
import ChatFooter from '../ChatFooter';
import {
  SDropzone,
  SFormContainer,
  SResizableContainer,
  SDropzoneText,
} from './ChatWidget.styled';
import { DEFAULT_SIZE } from 'constants/chat';
import { useForms } from 'hooks/useForms';
import { UploadFileIcon } from '@omilia/icon';
import { useDropFiles } from 'hooks/useDropFiles';
import { getChatContainer } from 'utils/getChatContainer';
import { useTranslation } from '@omilia/providers';

const ChatWidget = props => {
  const [fileWidth, setFileWidth] = useState(155);
  const { t } = useTranslation();

  const { onFormSubmit, onFormSkip, isFormDisplayed, formConfig } = useForms({
    isLoaded: props.isLoaded,
    isFormClosed: props.isFormClosed,
    feedback: props.feedback,
    openChat: props.openChat,
    sendMessage: props.sendMessage,
    closeChat: props.closeChatBox,
    welcomeForm: props.welcomeForm,
    isDialogEnded: props.isDialogEnded,
    matchFeedbackType: props.matchFeedbackType,
  });

  const {
    isDragActive,
    open,
    selectedFiles,
    setSelectedFiles,
    rootProps,
    inputProps,
  } = useDropFiles(props.attachmentsConfig);

  const showTranscriptDownload =
    props.enableTranscriptDownload && props.isDialogEnded;

  const handleResetFile = (fileName: string) => {
    setSelectedFiles(prevFiles =>
      prevFiles.filter(file => file.name !== fileName),
    );
  };

  const handleResetAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleRestartChat = () => {
    handleResetAllFiles();
    props.restartChat();
  };

  const handleFileWidth = () => {
    const shadowRoot = getChatContainer(props?.disableSecuredStyles);
    const width = shadowRoot.querySelector('.chat').clientWidth / 2;
    setFileWidth(width);
  };

  return (
    <div {...rootProps} role="region" aria-label={t('widget.chatWidget')}>
      <SResizableContainer
        className="chat"
        defaultSize={{
          ...DEFAULT_SIZE,
          ...(props.isPreviewMode && { height: 500 }),
          ...props.defaultSize,
        }}
        style={{
          position: 'fixed',
          ...(props.isPreviewMode && { bottom: '100px', right: '12px' }),
        }}
        minWidth={300}
        maxWidth={400}
        minHeight={310}
        onResize={handleFileWidth}
      >
        <ChatHeader {...props} restartChat={handleRestartChat} />
        {isFormDisplayed ? (
          <SFormContainer>
            <DynamicForm
              {...formConfig}
              onSubmit={onFormSubmit}
              onSkip={onFormSkip}
            />
          </SFormContainer>
        ) : (
          <>
            {(props.attachmentsConfig.attachmentsEnabled ||
              props.attachmentsConfig.attachmentsRichEnabled) &&
              isDragActive && (
                <SDropzone>
                  <UploadFileIcon
                    size="32"
                    primaryColor={colors.COLOR_DEEP_PURPLE_800}
                  />
                  <SDropzoneText>{t('widget.dropFilesHere')}</SDropzoneText>
                </SDropzone>
              )}
            <ChatContent
              {...props}
              uploadFile={open}
              files={selectedFiles}
              showDownload={showTranscriptDownload}
              backgroundColor={props.backgroundColor || colors.COLOR_WHITE}
            />
            <ChatFooter
              {...props}
              maxFiles={props.attachmentsConfig.maxFiles}
              fileWidth={fileWidth}
              resetFile={handleResetFile}
              resetAllFiles={handleResetAllFiles}
              uploadFile={open}
              selectedFiles={selectedFiles}
              fileInputProps={inputProps}
            />
          </>
        )}
      </SResizableContainer>
    </div>
  );
};

export default ChatWidget;
